/**
 * PlaybookRunsList - Paginated list of all playbook executions across the workspace.
 *
 * ## Purpose
 * Provides a global view of playbook execution history, useful for:
 * - Monitoring playbook activity across all conversations
 * - Debugging failed runs
 * - Understanding execution patterns across top-level and nested runs
 *
 * ## Top-Level Filter
 * The "Top-level runs only" checkbox filters out nested playbook runs:
 * - Checked (default): Shows only runs where `parentPlaybookRunId` is null
 * - Unchecked: Shows all runs including those spawned by `executePlaybook`
 *
 * This is useful because nested runs are often implementation details of a parent
 * playbook. Users typically care about top-level runs that represent complete workflows.
 *
 * ## Key Fields Displayed
 * - **Status**: Current execution state (running, awaiting_*, complete, error)
 * - **Error**: Hover to see error message if run failed
 *
 * @see PlaybookRunDetail for drill-down into individual runs
 * @see AllPlaybookRunsQuery for the GraphQL query with pagination
 */

import { PlaybookRunOrderByField, PlaybookRunSortOrder, useAllPlaybookRunsQuery } from "@qw/graphql";
import {
  Badge,
  Clippy,
  Column,
  ColumnContent,
  ColumnFooter,
  CondensedPagination,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  Empty,
  Error,
  Grid,
  GridCell,
  GridLoading,
  GridRowHeader,
  GridRowLink,
  Icon,
  MoreMenu,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  usePagination,
} from "@qw/ui";
import { format, formatDistanceToNow, formatDuration, intervalToDuration } from "date-fns";
import { parseAsBoolean, parseAsStringEnum, useQueryState } from "nuqs";
import type { MouseEvent } from "react";
import { Link } from "react-router";

const STATUS_CONFIG: Record<
  string,
  { label: string; emoji: string; variant: "secondary" | "destructive" | "green" | "outline" }
> = {
  running: { label: "Running", emoji: "⏳", variant: "secondary" },
  awaiting_user_input: { label: "Awaiting Input", emoji: "⏸️", variant: "secondary" },
  awaiting_child_playbook: { label: "Awaiting Child", emoji: "🔄", variant: "secondary" },
  complete: { label: "Complete", emoji: "✅", variant: "green" },
  error: { label: "Failed", emoji: "❌", variant: "destructive" },
  cancelled: { label: "Cancelled", emoji: "🚫", variant: "outline" },
};

const truncateId = (id: string, length = 8) => id.slice(0, length);

const PLAYBOOK_RUN_ORDER_BY_FIELDS = [
  PlaybookRunOrderByField.StartedAt,
  PlaybookRunOrderByField.Status,
  PlaybookRunOrderByField.PlaybookName,
] as const;

type PlaybookRunListOrderByField = (typeof PLAYBOOK_RUN_ORDER_BY_FIELDS)[number];

const DEFAULT_SORT: Record<PlaybookRunListOrderByField, PlaybookRunSortOrder> = {
  [PlaybookRunOrderByField.StartedAt]: PlaybookRunSortOrder.Desc,
  [PlaybookRunOrderByField.Status]: PlaybookRunSortOrder.Asc,
  [PlaybookRunOrderByField.PlaybookName]: PlaybookRunSortOrder.Asc,
};

const ORDER_BY_LABELS: Record<PlaybookRunListOrderByField, string> = {
  [PlaybookRunOrderByField.StartedAt]: "Started At",
  [PlaybookRunOrderByField.Status]: "Status",
  [PlaybookRunOrderByField.PlaybookName]: "Playbook Name",
};

export const PlaybookRunsList = () => {
  const { page, rowsPerPage, setNumRecords } = usePagination();

  const [topLevelOnly, setTopLevelOnly] = useQueryState("topLevelOnly", parseAsBoolean.withDefault(true));

  const [orderBy, setOrderBy] = useQueryState(
    "orderBy",
    parseAsStringEnum<PlaybookRunListOrderByField>([...PLAYBOOK_RUN_ORDER_BY_FIELDS]).withDefault(
      PlaybookRunOrderByField.StartedAt
    )
  );

  const [sort, setSort] = useQueryState(
    "sort",
    parseAsStringEnum<PlaybookRunSortOrder>(Object.values(PlaybookRunSortOrder)).withDefault(PlaybookRunSortOrder.Desc)
  );

  const handleOrderByChange = (selectedOrderBy: string) => {
    const field = selectedOrderBy as PlaybookRunListOrderByField;
    if (field === orderBy) {
      // Toggle direction
      void setSort(sort === PlaybookRunSortOrder.Asc ? PlaybookRunSortOrder.Desc : PlaybookRunSortOrder.Asc);
    } else {
      // New field with default direction
      void setOrderBy(field);
      void setSort(DEFAULT_SORT[field]);
    }
  };

  const { data, error, loading } = useAllPlaybookRunsQuery({
    variables: {
      input: {
        page: Number(page) || 1,
        rowsPerPage: Number(rowsPerPage) || 25,
        topLevelOnly,
        orderBy,
        sort,
      },
    },
    onCompleted: (data) => setNumRecords(data.allPlaybookRuns.numRecords),
    fetchPolicy: "cache-and-network",
  });

  if (error) {
    return <Error error={error} className="p-4" />;
  }

  const runs = data?.allPlaybookRuns.playbookRuns ?? [];

  const headerCells = (
    <>
      <GridCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">Run ID</span>
          </TooltipTrigger>
          <TooltipContent>Unique identifier for this playbook execution</TooltipContent>
        </Tooltip>
      </GridCell>
      <GridCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">Started</span>
          </TooltipTrigger>
          <TooltipContent>When this playbook run was started</TooltipContent>
        </Tooltip>
      </GridCell>
      <GridCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">Playbook</span>
          </TooltipTrigger>
          <TooltipContent>The playbook definition that was executed</TooltipContent>
        </Tooltip>
      </GridCell>
      <GridCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">User</span>
          </TooltipTrigger>
          <TooltipContent>User who triggered this playbook run</TooltipContent>
        </Tooltip>
      </GridCell>
      <GridCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">Status</span>
          </TooltipTrigger>
          <TooltipContent>Current execution status of the run</TooltipContent>
        </Tooltip>
      </GridCell>
      <GridCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">Error</span>
          </TooltipTrigger>
          <TooltipContent>Indicates if the run failed with an error</TooltipContent>
        </Tooltip>
      </GridCell>
    </>
  );

  return (
    <TooltipProvider>
      <Column className="flex-auto bg-background">
        <ColumnContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <MoreMenu icon="filter_list" buttonProps={{ size: "sm" }}>
              <DropdownMenuLabel>Filter</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={topLevelOnly ? "top-level" : "all"}
                onValueChange={(v) => void setTopLevelOnly(v === "top-level")}
              >
                <DropdownMenuRadioItem value="top-level">Top-level runs only</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="all">All runs (including nested)</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>

              <DropdownMenuSeparator />

              <DropdownMenuLabel>Sort By</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={orderBy} onValueChange={handleOrderByChange}>
                {PLAYBOOK_RUN_ORDER_BY_FIELDS.map((field) => (
                  <DropdownMenuRadioItem key={field} value={field} className="justify-between">
                    {ORDER_BY_LABELS[field]}
                    {orderBy === field && (
                      <Icon icon={sort === PlaybookRunSortOrder.Asc ? "arrow_upward" : "arrow_downward"} />
                    )}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </MoreMenu>
          </div>

          {loading && runs.length === 0 ? (
            <Grid className="grid-cols-[1fr_1fr_1.5fr_1fr_1fr_0.5fr]">
              <GridRowHeader position="sticky">{headerCells}</GridRowHeader>
              <GridLoading rows={5} columns={6} />
            </Grid>
          ) : runs.length === 0 ? (
            <Empty
              icon="play_circle"
              title="No playbook runs yet"
              description="Run a playbook from a conversation to see execution history here."
            />
          ) : (
            <Grid className="grid-cols-[1fr_1fr_1.5fr_1fr_1fr_0.5fr]">
              <GridRowHeader position="sticky">{headerCells}</GridRowHeader>

              {runs.map((run) => {
                const startedAt = new Date(run.startedAt);
                const finishedAt = run.finishedAt ? new Date(run.finishedAt) : null;
                const duration = finishedAt
                  ? formatDuration(intervalToDuration({ start: startedAt, end: finishedAt }), {
                      format: ["hours", "minutes", "seconds"],
                    }) || "< 1 second"
                  : null;
                const status = STATUS_CONFIG[run.status] ?? STATUS_CONFIG.error;

                return (
                  <GridRowLink key={run.id} to={`/playbooks/runs/${run.id}`} title="View run details">
                    <GridCell className="font-mono text-xs">
                      <span className="flex items-center gap-1">
                        {truncateId(run.id)}
                        <Clippy
                          content={run.id}
                          variant="ghost"
                          size="xs"
                          className="h-5 w-5"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </span>
                    </GridCell>
                    <GridCell className="text-xs text-muted-foreground">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">{format(startedAt, "MMM d, h:mm a")}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1">
                            <div>Started {formatDistanceToNow(startedAt, { addSuffix: true })}</div>
                            {duration && <div>Duration: {duration}</div>}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </GridCell>
                    <GridCell>
                      <span className="flex items-center gap-1">
                        <span className="truncate">{run.playbook?.name ?? "(untitled)"}</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={`/playbooks/${run.playbookId}`}
                              onClick={(e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
                              className="text-muted-foreground hover:text-primary"
                            >
                              <Icon icon="open_in_new" className="h-4 w-4" />
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent>View playbook definition</TooltipContent>
                        </Tooltip>
                      </span>
                    </GridCell>
                    <GridCell>
                      {run.user ? (
                        <span className="truncate">
                          {run.user.firstName} {run.user.lastName}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </GridCell>
                    <GridCell>
                      <Badge variant={status.variant}>
                        {status.emoji} {status.label}
                      </Badge>
                    </GridCell>
                    <GridCell>
                      {run.errorMessage ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Icon icon="error" className="text-destructive cursor-default" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <span className="line-clamp-3">{run.errorMessage}</span>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </GridCell>
                  </GridRowLink>
                );
              })}
            </Grid>
          )}
        </ColumnContent>
        <ColumnFooter className="justify-center">
          <CondensedPagination label="Runs" className="mx-auto" />
        </ColumnFooter>
      </Column>
    </TooltipProvider>
  );
};
